'use strict';
// @ts-check
/* global $ module */

declare var $;

import { IAddress } from '../tv_shipping/IAddress';
import { ICSCartJsonResponse } from '../tv_shipping/ICSCartJsonResponse';
import { Notifications } from '../tv_utils/notifications';
import { initialize_google_autocomplete } from '../tv_third_party_integrations/initialize_google_autocomplete';

/**
 * Handles interactions with an address book drop down and form.
 */
export class AddressBook {
    /**
     * @var {string} prefix used to denote the id prefix for the form elements,
     *  since you can have multiple address book controls on a page.
     * @var {[key: string]: any} elements dict of related HTMLElements on the dom.
     * @var {Function} callback to call when an address is selected.
     * @var {string} update_recid temporary storage to remember which recid was selected before update.
     * @var {boolean} residential flag for whether address is residential or commercial.
     */
    prefix: string;
    elements: {[key: string]: any};
    callback: Function;
    update_recid: string;
    residential: boolean = false;

    /**
     * Instantiate the class, bind the elements,
     * and initialize the fields if there is an address in the address book.
     *
     * @param {string} id used to create the prefix that relates to the elements for this control.
     * @param {Function} callback to call when an address is selected.
     * @return {AddressBook} instantiated and bound.
     */
    constructor(id: string, callback: Function) {
        this.prefix = 'js-' + id;
        this.elements = {};
        this.callback = callback;
        this.update_recid = '';

        this.bind();
        this.init_address();

        // If this is the Account Address Book page, init google autocomplete.
        if (document.getElementById('js-init-google-autocomplete')) {
            initialize_google_autocomplete();
        }
    }

    /**
     * Returns the currently selected, or entered address.
     *
     * @return {IAddress} with values from the form.
     */
    address(): IAddress {
        let country = this.elements.address.country_selected.value;
        if (!country) {
            country = this.elements.address.country.value;
        }
        let state = this.elements.address.state_selected.value;
        if (!state) {
            state = this.elements.address.state.value;
        }

        return {
            name: this.elements.address.name ? this.elements.address.name.value : '',
            company: this.elements.address.company ? this.elements.address.company.value : '',
            email: '',
            address1: this.elements.address.address1.value,
            address2: this.elements.address.address2.value,
            city: this.elements.address.city.value,
            state: state,
            zipcode: this.elements.address.zipcode.value,
            country: country,
            phone: this.elements.address.phone ? this.elements.address.phone.value : '',
            recid: (
                this.elements.drop_down && this.elements.drop_down.selectedIndex >= 0
            ) ? this.elements.drop_down[this.elements.drop_down.selectedIndex].value : '',
            is_primary: (this.elements.address.primary) ? this.elements.address.primary.value : false,
            residential: this.residential
        };
    }

    /**
     * Create the references and events on the related elements and controls.
     *
     * @return {void}
     */
    bind(): void {
        this.elements.drop_down = document.getElementById(this.prefix + '-address-book-ddl');
        this.elements.inputs_container = document.getElementById(this.prefix + '-address-inputs-container');

        this.elements.invalid_address_error = document.getElementById(this.prefix + '-address-error');
        this.elements.adjusted_address_messages = document.getElementsByClassName(this.prefix + '-address-warning');
        this.elements.states = document.getElementsByClassName(this.prefix + '-address-state');

        this.elements.address = {
            name: document.getElementById(this.prefix + '-address-name'),
            company: document.getElementById(this.prefix + '-address-company'),
            address1: document.getElementById(this.prefix + '-address-address1'),
            address2: document.getElementById(this.prefix + '-address-address2'),
            city: document.getElementById(this.prefix + '-address-city'),
            state: this.elements.states[0],
            state_selected: document.getElementById(this.prefix + '-address-state-selected'),
            zipcode: document.getElementById(this.prefix + '-address-zipcode'),
            country: document.getElementById(this.prefix + '-address-country'),
            country_selected: document.getElementById(this.prefix + '-address-country-selected'),
            phone: document.getElementById(this.prefix + '-address-phone'),
            primary: document.getElementById(this.prefix + '-address-primary')
        };

        this.elements.buttons = {
            save: document.getElementById(this.prefix + '-address-book-save-btn'),
            select: document.getElementById(this.prefix + '-address-book-select-btn'),
            edit: document.getElementById(this.prefix + '-address-book-edit-btn'),
            new: document.getElementById(this.prefix + '-address-book-new-btn'),
            delete: document.getElementById(this.prefix + '-address-book-delete-btn')
        };

        // Events.
        if (this.elements.drop_down) {
            this.elements.drop_down.onchange = this.select_address.bind(this);
        }

        if (this.elements.address.state) {
            this.elements.address.state.addEventListener('change', this.select_state.bind(this));
        }
        if (this.elements.address.country) {
            this.elements.address.country.addEventListener('change', this.select_country.bind(this));
        }

        // These elements don't exist when logged out.
        if (this.elements.buttons.save) {
            this.elements.buttons.save.addEventListener('click', this.save.bind(this));
        }

        if (this.elements.buttons.new) {
            this.elements.buttons.new.addEventListener('click', this.create.bind(this));
            // If no address is available, then we want to start off with a new delivery address.
            if (document.getElementById('js-billing-population') &&
                !(<HTMLInputElement> document.getElementById('js-billing-population')).value
            ) {
                this.elements.buttons.new.click();
            }
        }

        if (this.elements.buttons.edit) {
            this.elements.buttons.edit.addEventListener('click', this.edit.bind(this));
        }

        if (this.elements.buttons.delete) {
            this.elements.buttons.delete.addEventListener('click', this.delete.bind(this));
        }

    }

    /**
     * Wipes the values on the form and the drop down.
     *
     * @return {void}
     */
    clear(): void {
        this.elements.drop_down.selectedIndex = 0;

        if (this.elements.address.name !== null) {
            this.elements.address.name.value = '';
        }
        if (this.elements.address.company !== null) {
            this.elements.address.company.value = '';
        }
        this.elements.address.address1.value = '';
        this.elements.address.address2.value = '';
        this.elements.address.city.value = '';
        this.elements.address.state.selectedIndex = 0;
        this.elements.address.country.selectedIndex = 0;
        this.elements.address.zipcode.value = '';
        this.elements.address.phone.value = '';
        this.elements.address.primary.value = 'false';
        
        // Clear Error and warning messages.
        this.elements.invalid_address_error.classList.add('tv-display__none');
        this.toggle_delivery_address_adjusted_warning(false);
    }

    /**
     * Wipes the form and displays the fields so a new one can be entered.
     *
     * @return {void}
     */
    create(): void {
        this.clear();
        this.toggle_inputs(true);
    }

    /**
     * Displays the fields so the current address can be edited.
     *
     * @return {void}
     */
    edit(): void {
        this.toggle_inputs(true);
    }

    /**
     * If there is an address in the drop down, set the address to the fields and hide the inputs.
     *
     * @return {void}
     */
    init_address(): void {
        if (!this.elements.drop_down) {
            return;
        }

        const selectedIndex = this.elements.drop_down.selectedIndex;
        if (selectedIndex < 0) {
            return;
        }

        // Init a blank address.
        let selected_address: IAddress = {
            name: '',
            company: '',
            email: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            phone: '',
            recid: ''
        };

        // Unless the user has a primary address in the address book, then use that.
        if (this.elements.drop_down.options[selectedIndex].dataset.entry) {
            selected_address = JSON.parse(this.elements.drop_down.options[selectedIndex].dataset.entry);
        }

        this.set_address(selected_address);

        // TODO CSCART4 -- Need to figure out a way to only do this when logged in.
        // this.toggle_inputs(false);
    }

    /**
     * Get the recid for the primary address in the AddressBook.
     *
     * @return {string} the primary address recid.
     */
    get_primary_recid(): string {
        const options = this.elements.drop_down.children;
        for (let i = 0; i < options.length; i++) {
            if (options[i].value) {
                const data = JSON.parse(options[i].dataset.entry);
                if (data.primary) {
                    return data.recid;
                }
            }
        }
        return '';
    }

    /**
     * Save the shipping address to the address book in AX.
     *
     * @returns {void}
     */
    save(): void {
        this.elements.invalid_address_error.classList.add('tv-display__none');
        this.validate_address(this.update_address.bind(this));
    }

    /**
     * Callback to run after the address is validated to add it to AX if the address was valid.
     *
     * @param {boolean} address_valid flag for whether the address was valid or not.
     * @returns {void}
     */
    update_address(address_valid: boolean): void {
        if (!address_valid) {
            return;
        }

        this.update_recid = this.address().recid;

        // Get the ids of all of the addresses in the address book.
        const record_ids = [];
        for (const record of this.elements.drop_down.options) {
            if (record.dataset.entry) {
                const json_data = JSON.parse(record.dataset.entry);
                record_ids.push(json_data.recid);
            }
        }

        // Send the new address data to AX.
        $.ceAjax('request', 'index.php?dispatch=tv_address_book.update_address_data', {
            method: 'POST',
            result_ids: 'js-shipping-address-result',
            caching: false,
            data: this.address(),
            callback: response => {
                // Once done re-init the address book drop down so it has the new information.
                if (response) {
                    this.update_shipping_address_book(record_ids);
                }
            }
        });

        Notifications.set_notification(
            'N',
            "Notice",
            "Address saved to address book successfully."
        );
    }

    /**
     * Delete an address from the address book.
     *
     * @returns {void}
     */
    delete(): void {
        this.update_recid = this.address().recid;

        // Check the selected address to see if it's the primary address.
        const selectedIndex = this.elements.drop_down.selectedIndex;
        if (this.elements.drop_down.options[selectedIndex].dataset.entry) {
            const selected_address = JSON.parse(this.elements.drop_down.options[selectedIndex].dataset.entry);
            // If it's the primary address, return an error notification.
            if (selected_address.primary) {
                Notifications.set_notification(
                    'E',
                    "Can't delete primary address.",
                    "This address can't be deleted because it's set as your primary address."
                );
                return;
            }
            // Send the request to delete the address.
            const recid = selected_address.recid;
            const primary = selected_address.primary;
            $.ceAjax('request', 'index.php?dispatch=tv_address_book.delete_address', {
                method: 'POST',
                caching: false,
                data: {
                    recid: recid,
                    primary: primary
                },
                callback: response => {
                    if (response) {
                        this.after_delete_callback();
                    }
                }
            });
        }
    }

    /**
     * Update the address book drop-down and update the inputs after an address has been deleted.
     * @return {void}
     */
    after_delete_callback(): void {
        // Get the ids of all of the addresses in the address book.
        const record_ids = [];
        for (const record of this.elements.drop_down.options) {
            if (record.dataset.entry) {
                const json_data = JSON.parse(record.dataset.entry);
                record_ids.push(json_data.recid);
            }
        }
        // Set the notification that the address has been deleted.
        Notifications.set_notification(
            'N',
            "Selected address has been deleted.",
            "The address you've selected has been deleted."
        );

        // Update the inputs and address book drop-down.
        this.clear();
        this.update_shipping_address_book(record_ids);
    }

    /**
     * Hides the form and drop down, then sends the selected address to the callback.
     *
     * @return {void}
     */
    select_address(): void {
        this.init_address();
        this.elements.invalid_address_error.classList.add('tv-display__none');

        if (document.getElementById('js-name-phone-container')) {
            document.getElementById('js-name-phone-container').classList.add('hidden');
        }

        if (this.callback) {
            this.callback(this.address());
        }
    }

    /**
     * Updates the country field when the country drop down is changed.
     *
     * @return {void}
     */
    select_country(): void {
        const idx = this.elements.address.country.selectedIndex;
        this.elements.address.country_selected.value = this.elements.address.country[idx].value;
    }

    /**
     * Updates the state field when the state drop down is changed.
     *
     * @return {void}
     */
    select_state(): void {
        const idx = this.elements.address.state.selectedIndex;
        this.elements.address.state_selected.value = this.elements.address.state[idx].value;
    }

    /**
     * Sets the address fields, and drop downs to the passed address.
     *
     * @param {IAddress} address to set.
     * @return {void}
     */
    set_address(address: IAddress): void {
        for (const field in address) {
            if (field === "country") {
                // The country has to be set via drop down.
                const value = address[field];
                this.elements.address.country_selected.value = value;
                for (const option of this.elements.address.country) {
                    // Only make a change if we need to.
                    if (option.value === value && this.elements.address.country.selectedIndex !== option.index) {
                        this.elements.address.country.selectedIndex = option.index;

                        /**
                         * Then trigger the native cscart change event to
                         * fill the states drop down based on the country.
                         */
                        this.elements.address.country.dispatchEvent(
                            new CustomEvent('change', { bubbles: true, cancelable: false, detail: null })
                        );
                    }
                }

                // Then set the state.
                this.elements.address.state_selected.value = address['state'];
                for (const option of this.elements.address.state) {
                    if (option.value === address['state']) {
                        this.elements.address.state.selectedIndex = option.index;
                    }
                }
            } else if (this.elements.address.hasOwnProperty(field) && this.elements.address[field]) {
                this.elements.address[field].value = address[field];
            } else if (field === 'is_primary') { // TODO : need to double check this.
                // Check the primary field and if it's the primary address, hide the delete button.
                if (this.elements.buttons.delete) {
                    this.elements.buttons.delete.style.display = 'block';
                    const value = address[field];
                    if (value) {
                        this.elements.buttons.delete.style.display = 'none';
                    }
                }
                this.elements.address.primary.value = address[field];
            } else if (field === 'street') {
                this.elements.address.address1.value = address[field]
                this.elements.address.address2.value = '';
            } else if (field === 'attn') { // Addresses are saved differently in AX.
                this.elements.address.name.value = address.attn;
                this.elements.address.company.value = address.name;
            } else if (field === 'zip') {
                this.elements.address.zipcode.value = address.zip;
            }
        }
    }

    /**
     * Shows or hides the address fields.
     *
     * @param {boolean} show true to display.
     * @param {boolean} disable true to disable.
     * @return {void}
     */
    toggle_inputs(show: boolean, disable: boolean = false): void {
        const select_names = ['state', 'country'];

        for (const element_name in this.elements.address) {
            // If the element doesn't exist, or it is a special "select value holder" element, continue.
            if (!this.elements.address[element_name] ||
                element_name.endsWith('_selected') || (
                    this.elements.address[element_name].type === 'hidden' &&
                    this.elements.address[element_name].getAttribute('cloned') !== 'true'
                )
            ) {
                continue;
            }

            if (show) {
                if (select_names.indexOf(element_name) === -1) {
                    this.elements.address[element_name].classList.remove('hidden');
                }
                this.elements.address[element_name].removeAttribute('disabled');
            } else {
                if (select_names.indexOf(element_name) === -1) {
                    this.elements.address[element_name].classList.add('hidden');
                }
                if (disable) {
                    this.elements.address[element_name].setAttribute('disabled', 'disabled');
                }
            }
        }

        const display_mode = show ? 'block' : 'none';
        if (this.elements.buttons.save) {
            this.elements.buttons.save.style.display = 'inline-block';
        }
        this.elements.inputs_container.style.display = display_mode;
    }

    /**
     * Makes the request to cscart to get the new address drop down values,
     * when an address' information is added or changed.
     *
     * @param {Array<number>} address_record_ids The ids of address book entries before the update.
     * @return {void}
     */
    update_shipping_address_book(address_record_ids : Array<number>): void {
        const url = 'index.php?dispatch=tv_shipping.update_shipping_address_book';
        $.ceAjax('request', url, {
            method: 'POST',
            result_ids: 'js-shipping-address-book-ddl-container',
            caching: false,
            data: {
                address_record_ids: address_record_ids
            },
            callback: response => {
                if (response) {
                    this.update_shipping_address_book_callback(response);
                }
            }
        });
    }

    /**
     * Use the new address information from the response to populate the drop down.
     *
     * @param {ICSCartJsonResponse} response The response that includes the rec id of the new address.
     * @return {void}
     */
    update_shipping_address_book_callback(response: ICSCartJsonResponse): void {
        this.elements.drop_down = document.getElementById(this.prefix + '-address-book-ddl');
        if (this.elements.drop_down) {
            this.elements.drop_down.onchange = this.select_address.bind(this);
        }

        const option_values = [];
        for (const record of this.elements.drop_down.options) {
            option_values.push(record.value);
            if (record.hasAttribute('selected')) {
                record.removeAttribute('selected');
            }
            if (record.dataset.entry) {
                const json_data = JSON.parse(record.dataset.entry);

                // Select the new/edited address.
                if (json_data.recid === response.json.new_record_id ||
                    json_data.recid === this.update_recid
                ) {
                    record.setAttribute('selected', 'selected');

                    // Update the caller with the updated address.
                    if (this.callback){
                        this.callback(this.address());
                    }

                    // If it's not the primary address, display the delete button.
                    if (!json_data.primary && this.elements.buttons.delete) {
                        this.elements.buttons.delete.style.display = 'block';
                    }
                }
            }
        }
        // If the updated record/option isn't in the array of values, select the first option.
        if (!option_values.includes(this.update_recid)) {
            this.elements.drop_down.selectedIndex = 0;
        }

        if (document.getElementById('js-name-phone-container')) {
            document.getElementById('js-name-phone-container').classList.add('hidden');
        }
    }

    /**
     * Reaches out to the shipping service and validates an address.
     *
     * @param {Function} callback to handle the response when done.
     * @returns {void}
     */
    validate_address(callback: Function): void {
        $.ceAjax('request', 'index.php?dispatch=tv_shipping.validate_address', {
            method: 'POST',
            message: 'Validating address',
            caching: false,
            data: {
                address: this.address()
            },
            callback: this.validate_address_callback.bind(this, callback)
        });
    }

    /**
     * If the address is valid, update the UI then fetch the shipping estimates.
     * Otherwise, display the warnings.
     *
     * @param {Function} callback to call with the result.
     * @param {ICSCartJsonResponse} response from the native AJAX.
     * @returns {void}
     */
    validate_address_callback(callback: Function, response: ICSCartJsonResponse) : void {
        let valid = true;
        const data: {
            status: string,
            address: IAddress,
            adjusted: boolean,
            message: string,
            residential: boolean
        } = response.json;

        // Check the status and the phone number property of the address since a phone number is required for validation.
        // Name (attn in AX) is required for the webstore itself, but not a UPS or FedEx address, so it should be checked as well.
        if (data.status === 'valid' && data.address.phone && data.address.attn) {
            this.set_address(data.address);
            this.elements.invalid_address_error.classList.add('tv-display__none');
            if (document.getElementById('js-delivery-address-warning')) {
                document.getElementById('js-delivery-address-warning').classList.add('tv-display__none');
            }
            this.toggle_delivery_address_adjusted_warning(data.adjusted);
        } else if (data.status === 'invalid' || !data.address.phone || !data.address.attn) {
            valid = false;
            if (document.getElementById('js-delivery-address-warning')) {
                document.getElementById('js-delivery-address-warning').classList.add('tv-display__none');
            }
            this.toggle_delivery_address_adjusted_warning(false);
            this.validate_input();
        } else if (data.status === 'warning' ) {
            this.set_address(data.address);
            this.elements.invalid_address_error.classList.add('tv-display__none');
            this.toggle_delivery_address_adjusted_warning(data.adjusted);
        }
        this.residential = data.residential;

        !callback || callback(valid, data.adjusted, data.message);
    }

    /**
     * Show or hide the warning for when a delivery address is adjusted by the carrier,
     * during address validation.
     *
     * @param {boolean} show when true.
     * @returns {void}
     */
    toggle_delivery_address_adjusted_warning(show: boolean): void {
        if (show === true) {
            for (const element of this.elements.adjusted_address_messages) {
                element.classList.remove('tv-display__none');
            }
        } else {
            for (const element of this.elements.adjusted_address_messages) {
                element.classList.add('tv-display__none');
            }
        }
    }

    /**
     * Method to draw attention to a required field that is not valid.
     * @returns {void}
     */
    validate_input() : void {
        const address = this.elements.address;
        for (const field in address) {
            if (!address[field].validity.valid) {
                address[field].focus();
                address[field].reportValidity();
                return;
            }
        }
    }
}

export function init() {
    new AddressBook('delivery', null);
}
